// src/components/StaffSectionForm.jsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export default function StaffSectionForm({ section }) {
  const navigate = useNavigate();

  // Group fields by subheading
  const subGroups = useMemo(() => {
    const map = {};
    (section.fields || []).forEach(f => {
      if (!map[f.subheading]) map[f.subheading] = [];
      map[f.subheading].push(f);
    });
    return Object.entries(map).map(([sub, fields]) => ({ subheading: sub, fields }));
  }, [section.fields]);

  // Value state
  const [values, setValues] = useState({});
  const handleChange = (sub, label, val) =>
    setValues(v => ({ ...v, [`${sub}:${label}`]: val }));

  // Computed totals
  const card = parseFloat(values['Card:Total'] || 0);
  const cash = parseFloat(values['Cash:Total'] || 0);
  const online = parseFloat(values['Online:Total'] || 0);
  const cardCashTotal = card + cash;
  const finalTotal = cardCashTotal + online;

  // Form completeness
  const allFilled = subGroups.every(g =>
    g.fields.every(f => values[`${g.subheading}:${f.label}`] !== undefined && values[`${g.subheading}:${f.label}`] !== '')
  );
  const canSubmit = allFilled && !isNaN(cardCashTotal) && !isNaN(finalTotal);

  // Modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  // Perform actual submission
  const doSubmit = async () => {
    await addDoc(collection(db, 'submissions'), {
      sectionId: section.id,
      sectionName: section.name,
      values: {
        ...values,
        'Card + Cash Total': cardCashTotal,
        'Final Total': finalTotal,
      },
      submittedAt: serverTimestamp(),
      storeId: localStorage.getItem('storeId'),
      submittedBy: localStorage.getItem('staffName') || 'Unknown',
    });
    setConfirmOpen(false);
    setSuccessOpen(true);
  };

  return (
    <div style={{
      maxWidth: 600,
      margin: '40px auto',
      padding: 20,
      border: '1px solid #ddd',
      borderRadius: 8,
      background: '#fff',
      position: 'relative'
    }}>
      <h2 style={{ marginTop: 0 }}>{section.name}</h2>

      {subGroups.map(group => (
        <div key={group.subheading} style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{group.subheading}</div>
          {group.fields.map(f => {
            const key = `${group.subheading}:${f.label}`;
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ flex: 1 }}>{f.label}</label>
                <input
                  type={f.type === 'number' ? 'number' : 'text'}
                  value={values[key] || ''}
                  onChange={e => handleChange(group.subheading, f.label, e.target.value)}
                  style={{
                    flex: 2,
                    padding: '6px 8px',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    marginRight: 12
                  }}
                />
                <span style={{ fontStyle: 'italic', color: '#888' }}>({f.type})</span>
              </div>
            );
          })}
        </div>
      ))}

      {/* Computed totals */}
      <div style={{ marginBottom: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span>Card + Cash Total</span>
          <strong>{cardCashTotal.toFixed(2)}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Final Total</span>
          <strong>{finalTotal.toFixed(2)}</strong>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <button
          onClick={() => navigate('/staff-dashboard')}
          style={{
            padding: '8px 12px',
            background: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}
        >
          ← Back
        </button>
        <button
          disabled={!canSubmit}
          onClick={() => setConfirmOpen(true)}
          style={{
            padding: '8px 12px',
            background: canSubmit ? '#28a745' : '#ccc',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: canSubmit ? 'pointer' : 'not-allowed'
          }}
        >
          Submit
        </button>
      </div>

      {/* Confirm Modal */}
      {confirmOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', padding: 20, borderRadius: 6, width: '90%',
            maxWidth: 320, textAlign: 'center'
          }}>
            <p style={{ marginBottom: 20 }}>Submit these totals?</p>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => setConfirmOpen(false)}
                style={{
                  flex: 1, marginRight: 8, padding: '8px 0',
                  border: '1px solid #6c757d', background: 'transparent',
                  color: '#6c757d', borderRadius: 4
                }}
              >
                Cancel
              </button>
              <button
                onClick={doSubmit}
                style={{
                  flex: 1, marginLeft: 8, padding: '8px 0',
                  background: '#28a745', color: '#fff', border: 'none', borderRadius: 4
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#fff', padding: 20, borderRadius: 6, width: '90%',
            maxWidth: 320, textAlign: 'center'
          }}>
            <p style={{ marginBottom: 20 }}>Submitted!</p>
            <button
              onClick={() => {
                setSuccessOpen(false);
                navigate('/staff-dashboard');
              }}
              style={{
                padding: '8px 16px', background: '#007bff', color: '#fff',
                border: 'none', borderRadius: 4, cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
